import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as Icons from "lucide-react";
import { useFormContext } from "react-hook-form";
import FilterProvider, {
  FilterState,
  normalizeFilterInput,
} from "./(overview)/(context)/filter";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import React from "react";

type Props = {
  onRefetchReady?: (fn: () => void) => void;
};

const Overview = ({ onRefetchReady }: Props) => {
  const { trpc } = useTRPC();
  const { watch, setValue } = useFormContext<FilterState>();
  const filterState = watch();

  const query = useQuery({
    queryKey: ["reports", filterState],
    queryFn: async () => {
      const reports = await trpc.reports.search.query();
      return reports;
    },
    enabled: false,
  });

  React.useEffect(() => {
    onRefetchReady?.(() => query.refetch());
  }, [query.refetch]);

  React.useEffect(() => {
    query.refetch();
  }, [filterState.page]);

  return (
    <div className="h-full overflow-auto">
      <div className="flex flex-col justify-between h-full gap-4">
        <div className="flex items-center justify-between gap-4">
          <Input
            placeholder="Zoek naar rapport"
            startContent={<Icons.Search className="w-4 h-4" />}
            className="w-fit"
          />                      
        </div>
        <div className="bg-foreground/10 rounded-md border p-2 overflow-auto h-full">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type rapport</TableHead>
                <TableHead>Speler</TableHead>
                <TableHead>Datum ingediend</TableHead>
                <TableHead>Ingediend door</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query?.data?.reports?.map((report, i) => (
                <TableRow key={i}>
                  <TableCell>{report?.type}</TableCell>
                  <TableCell>XXXX</TableCell>
                  <TableCell>XXXX</TableCell>
                  <TableCell>XXXX</TableCell>
                  <TableCell>XXXX</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm font-medium text-foreground/50">
            {query?.data?.total} rijen gevonden
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              onClick={() => setValue("page", 1)}
              disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
            >
              <Icons.ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                setValue("page", Math.max(1, Number(filterState.page) - 1))
              }
              disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
            >
              <Icons.ChevronLeft className="w-4 h-4" />
            </Button>
            <Input
              value={filterState.page}
              onChange={(e) => {
                setValue("page", e.currentTarget.valueAsNumber);
              }}
              onBlur={() => {
                query.refetch();
              }}
              type="number"
              min={1}
              max={query?.data?.pages || 1}
            />
            <Button
              variant="outline"
              onClick={() =>
                setValue(
                  "page",
                  Math.min(
                    Number(query?.data?.pages || 1),
                    Number(filterState.page) + 1
                  )
                )
              }
              disabled={Number(filterState.page) >= Number(query?.data?.pages)}
            >
              <Icons.ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setValue("page", query?.data?.pages || 1)}
              disabled={Number(filterState.page) >= Number(query?.data?.pages)}
            >
              <Icons.ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ({ ...props }: Props) => {
  return (
    <FilterProvider>
      <Overview {...props} />
    </FilterProvider>
  );
};
